import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { BadgeStatut, ValeurChamp, STATUTS } from "./BibValeur";
import { readHtmlMeta, fmtDateHeure } from "../services/bibliotheque";

// ===================================================================
// The Crowd v2 — Bibliotheques : rendu d'une fiche
//
// Markdown : remark-gfm + rehype-raw, avec les enrichissements existants
// ([Statut] -> badge, blockquote -> alerte selon son texte, code inline
// -> etiquette). HTML (procedures) : injecte tel quel, comme dans
// l'interface actuelle — voir la note securite de la fiche de pilotage.
// ===================================================================

const STATUS_REGEX = new RegExp(`\\[(${STATUTS.join("|")})\\]`, "g");
const STATUS_SEUL = new RegExp(`^\\[(${STATUTS.join("|")})\\]$`);

function TextWithBadges({ children }) {
  if (typeof children !== "string") return children;
  const parts = [];
  let lastIndex = 0;
  let match;
  STATUS_REGEX.lastIndex = 0;
  while ((match = STATUS_REGEX.exec(children)) !== null) {
    if (match.index > lastIndex) parts.push(children.slice(lastIndex, match.index));
    parts.push(<BadgeStatut key={match.index} status={match[1]} />);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < children.length) parts.push(children.slice(lastIndex));
  return parts.length > 0 ? <>{parts}</> : children;
}

function applyBadges(children) {
  if (Array.isArray(children)) return children.map((c, i) => (typeof c === "string" ? <TextWithBadges key={i}>{c}</TextWithBadges> : c));
  if (typeof children === "string") return <TextWithBadges>{children}</TextWithBadges>;
  return children;
}

function extractText(c) {
  if (typeof c === "string") return c;
  if (Array.isArray(c)) return c.map(extractText).join("");
  if (c?.props?.children) return extractText(c.props.children);
  return "";
}

// react-markdown transmet `node` : on l'ecarte pour ne pas le poser en attribut
const mdComponents = {
  td: ({ node, children, ...p }) => <td {...p}>{applyBadges(children)}</td>,
  p: ({ node, children, ...p }) => <p {...p}>{applyBadges(children)}</p>,
  li: ({ node, children, ...p }) => <li {...p}>{applyBadges(children)}</li>,
  code: ({ node, inline, children, className, ...p }) => {
    if (inline || !className) {
      const text = (Array.isArray(children) ? children.join("") : String(children || "")).trim();
      const m = text.match(STATUS_SEUL);
      if (m) return <BadgeStatut status={m[1]} />;
      return <code className="v2-md-etiquette" {...p}>{children}</code>;
    }
    return <code className={className} {...p}>{children}</code>;
  },
  blockquote: ({ node, children, ...p }) => {
    const text = extractText(children);
    let ton = "defaut";
    if (text.includes("Attention")) ton = "warning";
    if (text.includes("OK")) ton = "success";
    return <blockquote className={`v2-md-alerte v2-md-alerte-${ton}`} {...p}>{children}</blockquote>;
  },
};

// --- Bandeau front matter (fiches Markdown) ---

export function BandeauFrontMatter({ data, configFields }) {
  if (!data || !configFields) return null;
  const visibles = configFields.filter((f) => f.bandeau && (data[f.cle] || data[f.cle] === 0));
  if (visibles.length === 0) return null;
  return (
    <div className="v2-bib-bandeau">
      {visibles.map((f) => (
        <div key={f.cle} className="v2-bib-bandeau-item">
          <span className="v2-bib-bandeau-libelle">{f.label}</span>
          <span className="v2-bib-bandeau-valeur">
            <ValeurChamp type={f.type} valeur={data[f.cle]} />
          </span>
        </div>
      ))}
    </div>
  );
}

// --- Bandeau metadonnees (procedures HTML) ---

export function BandeauProcedure({ html }) {
  const meta = readHtmlMeta(html);
  if (!meta.created_at && !meta.updated_at) return null;
  const items = [
    meta.created_by && { label: "Créé par", value: meta.created_by },
    meta.created_at && { label: "Créé le", value: fmtDateHeure(meta.created_at) },
    meta.updated_by && { label: "Modifié par", value: meta.updated_by },
    meta.updated_at && { label: "Modifié le", value: fmtDateHeure(meta.updated_at) },
  ].filter(Boolean);
  return (
    <div className="v2-bib-bandeau">
      {items.map(({ label, value }) => (
        <div key={label} className="v2-bib-bandeau-item">
          <span className="v2-bib-bandeau-libelle">{label}</span>
          <span className="v2-bib-bandeau-valeur">{value}</span>
        </div>
      ))}
    </div>
  );
}

// --- Corps de fiche ---

export function CorpsMarkdown({ body }) {
  return (
    <div className="v2-bib-contenu">
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>
        {body}
      </ReactMarkdown>
    </div>
  );
}

export function CorpsHtml({ html }) {
  return <div className="v2-bib-contenu" dangerouslySetInnerHTML={{ __html: html }} />;
}

// Apercu Markdown simple (editeur split) : memes styles, sans enrichissements
export function ApercuMarkdown({ source }) {
  return (
    <div className="v2-bib-contenu">
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
        {source}
      </ReactMarkdown>
    </div>
  );
}
